import { useMemo } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useMy, UseMyOptions } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { Account, LinkAccount } from '@/types';
import { useMutation } from '@/utils/api';
import { getChariotWebhookURL } from '@/utils/integration.util';
import { capitalize } from '@/utils/lodash.util';

/**
 * Modify account integration with automatic success toast
 *
 * Default behavior: Shows appropriate toast based on action (connected/disconnected/updated)
 * Override: Pass ignoreToast: true to suppress all toasts (legacy parameter)
 * OR: Pass { silent: true } in variables to suppress all toasts (success + error)
 *
 * Note: Migrating to silent pattern, but keeping ignoreToast for backwards compatibility.
 *
 * @param action - Type of modification: 'link', 'unlink', 'update', or 'updateSetting'
 * @param email - Optional email for impersonation
 * @param ignoreToast - Legacy: suppress all toasts (success + error)
 *
 * @example
 * // Standard usage (automatic toast)
 * const { mutate: link } = useModifyAccount('link');
 * link({ username: 'github', config: { token: 'xxx' } });
 *
 * @example
 * // Silent update (no success toast, no error toast - component handles toasts)
 * link({ username: 'github', config: { token: 'xxx' }, silent: true });
 *
 * @example
 * // Legacy: suppress all toasts
 * const { mutate: link } = useModifyAccount('link', undefined, true);
 */
export const useModifyAccount = (
  action: 'link' | 'unlink' | 'update' | 'updateSetting',
  email?: string,
  ignoreToast?: boolean
) => {
  const { me, api, friend } = useAuth();
  const axios = useAxios({ doNotImpersonate: email === me, email });
  const { invalidate: invalidateAccount } = useMy(
    { resource: 'account' },
    { enabled: false, doNotImpersonate: email === me, email }
  );

  return useMutation<Account, Error, LinkAccount & { silent?: boolean }>({
    defaultErrorMessage: 'Failed to modify account',
    mutationFn: async account => {
      try {
        const {
          username,
          config: configAccount,
          member,
          value,
          key,
          role,
          settings,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          silent: _silent,
          ...rest
        } = account;
        const config =
          configAccount || (Object.keys(rest).length ? rest : null);
        if (config) {
          Object.entries(config).forEach(([key, value]) => {
            if (typeof value === 'boolean') {
              config[key] = String(value);
            }
          });
        }
        let data: Account;

        if (action === 'link' || action === 'updateSetting') {
          const response = await axios.post(`/account/${username}`, {
            secret: config,
            value,
            role,
            settings,
          });
          data = response.data as Account;
        } else if (action === 'update') {
          const response = await axios.put(`/account/${username}`, {
            value,
            key,
            settings,
          });
          data = response.data as Account;
        } else {
          const response = await axios.delete(`/account/${username}`, {
            data: {
              secret: configAccount,
              member,
              value,
              key,
              settings,
            },
          });
          data = response.data as Account;
        }

        return data;
      } catch (error: any) {
        // Show error toast unless suppressed by ignoreToast OR silent flag
        if (!ignoreToast && !account.silent) {
          const actionText =
            action === 'link'
              ? 'connecting'
              : action === 'updateSetting' || action === 'update'
                ? 'updating'
                : 'disconnecting';

          // Attempt to extract error message for better UX handling
          let errorMessage =
            'Please check your username and secret and try again.';
          if (typeof error.response?.data === 'string' && error.response.data) {
            errorMessage = error.response.data;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }

          // Check if it's a failed to trust SSO error (likely TXT record issue)
          if (errorMessage.toLowerCase().includes('failed to trust sso')) {
            errorMessage = 'TXT record verification failed.';
          }

          toast.error(`Error ${actionText} account`, {
            description: errorMessage,
          });
        }
        throw error;
      }
    },
    onSuccess: (data, account) => {
      const { username } = account;

      const snackbarTitle =
        username in AllIntegrations ? 'integration' : 'account';

      const snackbarAction =
        action === 'link'
          ? 'connected'
          : action === 'updateSetting' || action === 'update'
            ? 'updated'
            : 'disconnected';

      if (action === 'link' && username === 'hook') {
        const webhookUrl = getChariotWebhookURL({
          api,
          me: email || friend || me,
          pin: data.value,
        });
        navigator.clipboard.writeText(webhookUrl);
      }

      // Invalidate account queries with broad matching to cover all contexts:
      // - Single-tenant queries (Settings page with specific email)
      // - All-tenants queries (Dashboard with allTenants: true)
      // - Impersonated queries (with friend context)
      invalidateAccount();
      queryClient.invalidateQueries({
        predicate: query => {
          // Invalidate all account queries regardless of additional parameters
          // This ensures both ['MY', 'account', ...] and ['MY', 'account', ..., true] are invalidated
          // Note: UniqueQueryKeys.MY = 'MY' (uppercase)
          const key = query.queryKey as any[];
          return key[0] === 'MY' && key[1] === 'account';
        },
      });

      // Also invalidate asset queries as before
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });

      // Show success toast unless suppressed by ignoreToast (legacy) or silent flag
      if (!ignoreToast && !account.silent) {
        // Show success snackbar
        toast.success(`${capitalize(snackbarTitle)} ${snackbarAction}`, {
          description:
            username === 'hook'
              ? action === 'link'
                ? 'Webhook URL copied to clipboard.'
                : 'Webhook URL was destroyed.'
              : `Your ${snackbarTitle} has been successfully ${snackbarAction}.`,
        });
      }
    },
  });
};

export const useUserSettings = (
  options?: UseMyOptions<'account'>,
  queryKey: string = 'settings'
) => {
  const { isImpersonating, me, friend } = useAuth();

  const myQuery = useMy(
    {
      resource: 'account',
      query: `#${isImpersonating ? friend : me}#${queryKey}`,
    },
    options
  );

  return myQuery;
};

export function usePurgeAccount() {
  const axios = useAxios();
  const { logout } = useAuth();

  return useMutation<null, Error, void>({
    defaultErrorMessage: `Failed to purge account`,
    mutationFn: async () => {
      try {
        await axios.delete(`/account/purge`);
        logout();
      } catch {
        // ignore
      }
      return null;
    },
  });
}

export function useGetCollaboratorsListFromAccounts(accounts: Account[]) {
  return useMemo(() => {
    return accounts.filter(
      acc => !acc.key.endsWith('#settings#') && acc?.member === acc?.username
    );
  }, [accounts]);
}

export function useGetCollaboratorEmails(accounts: Account[]) {
  return useMemo(() => {
    return accounts
      .filter(
        acc => !acc.key.endsWith('#settings#') && acc?.member === acc?.username
      )
      .map(acc => acc.name);
  }, [accounts]);
}
